/**
 * FS-04-03 규칙 힌트 1개(SR-ACH-04 · SCR v0.6 개정 4 — 속도·출력 어휘, 기어비 UI·문구 없음 · Q-5 · SR-MODE-03).
 * 커널이 판 끝(fail)·추락(respawn fall)에 준 실패 분석(FailAnalysis — protocol.ts S5)의 pattern 으로
 * 카탈로그(`GET /api/catalog?type=hint_rule` — payload {pattern, principle})의 규칙을 고른다(LLM 없음).
 *  - 한 번에 1개. 같은 힌트를 연달아 두 번 보이지 않는다(직전 힌트는 탭 세션 동안 기억).
 *  - 맞는 규칙이 없으면 pattern 'unknown' 규칙 → 로컬 대체 규칙(셸 문구 hint.pattern.<pattern>).
 *  - 문구: 셸 문구 hint.rule.<key>.<se|su>(밀도별) → 카탈로그 i18n(로캘) → hint.pattern.<pattern>.<se|su>.
 *  - 기어·기어비 어휘가 든 카탈로그 문구는 쓰지 않는다(Q-5 방어 — 서버도 저장 때 거른다).
 */
import type { FailAnalysis, FailPattern } from "@picnic-field/local-host/protocol";
import type { Locale, Translate } from "../../../i18n";
import type { HintRule } from "../types";

export const FAIL_PATTERNS: readonly FailPattern[] = [
  "slip-start",
  "overspeed-fall",
  "stuck-step",
  "overturn",
  "off-course",
  "stall-load",
  "slow-timeout",
  "unknown",
];

export const isFailPattern = (s: unknown): s is FailPattern =>
  typeof s === "string" && (FAIL_PATTERNS as readonly string[]).includes(s);

/** 기어비 조언 금지(Q-5) — 이런 어휘가 든 카탈로그 문구는 버린다 */
const GEAR = /기어|톱니|gear/i;

/** 카탈로그 응답을 너그럽게 읽는다({items:[{key, payload:{pattern, principle}, i18n}]} · 배열 · S3 {data:{pattern}}) */
export function readHintRules(v: unknown): HintRule[] {
  const list: unknown[] = Array.isArray(v)
    ? v
    : Array.isArray((v as { items?: unknown })?.items)
      ? ((v as { items: unknown[] }).items ?? [])
      : [];
  const out: HintRule[] = [];
  for (const raw of list) {
    const o = (raw ?? {}) as Record<string, unknown>;
    if (o.type != null && o.type !== "hint_rule") continue;
    if (o.enabled === false) continue;
    const p = (o.payload ?? o.data ?? {}) as Record<string, unknown>;
    const key = typeof o.key === "string" ? o.key : null;
    const pattern = p.pattern ?? o.pattern;
    if (!key || !isFailPattern(pattern)) continue;
    const i18n = (o.i18n ?? null) as { ko?: unknown; en?: unknown } | null;
    const text = i18n
      ? {
          ko: typeof i18n.ko === "string" ? i18n.ko : undefined,
          en: typeof i18n.en === "string" ? i18n.en : undefined,
        }
      : null;
    if (text && [text.ko, text.en].some((s) => s && GEAR.test(s))) continue;
    const principle = (p.principle ?? p.principleTag ?? o.principleTag) as string | undefined;
    out.push({ key, pattern, principle, text });
  }
  return out;
}

/** 로컬 대체 규칙 — 패턴마다 하나(문구 hint.pattern.<pattern>.<se|su>) */
export const LOCAL_RULES: readonly HintRule[] = FAIL_PATTERNS.map((pattern) => ({
  key: `local-${pattern}`,
  pattern,
  local: true,
}));

/**
 * 규칙 고르기 — 이 패턴의 규칙 중 직전(last)과 다른 첫 규칙 → 'unknown' 규칙(직전과 다른) → 로컬 대체(직전과 다른).
 * 모두 직전과 같으면 null(힌트 없음 — 연속 반복 금지가 우선).
 */
export function pickHint(
  pattern: FailPattern | null,
  rules: readonly HintRule[],
  last: string | null,
): HintRule | null {
  if (!pattern) return null;
  const pools = [
    rules.filter((r) => r.pattern === pattern),
    pattern === "unknown" ? [] : rules.filter((r) => r.pattern === "unknown"),
    LOCAL_RULES.filter((r) => r.pattern === pattern),
    LOCAL_RULES.filter((r) => r.pattern === "unknown"),
  ];
  for (const pool of pools) {
    const r = pool.find((x) => x.key !== last);
    if (r) return r;
  }
  return null;
}

/** 힌트 문구(자유 텍스트 0 — 셸 문구 키 또는 운영자 카탈로그 문구만) */
export function hintText(
  t: Translate,
  locale: Locale,
  density: "se" | "su",
  rule: HintRule,
  has: (key: string) => boolean,
): string {
  const own = `hint.rule.${rule.key}.${density}`;
  if (!rule.local && has(own)) return t(own);
  const cat = rule.text?.[locale] ?? rule.text?.ko;
  if (!rule.local && cat && cat.trim()) return cat;
  return t(`hint.pattern.${rule.pattern}.${density}`);
}

/**
 * 이번 판의 실패 패턴 — 커널 fail 분석 → 마지막 추락 분석 → 이유로 추정(timeout → slow-timeout · 그 밖 unknown).
 * 완주면 null.
 */
export function failAnalysisOf(run: {
  result: { completed: boolean; reason: string } | null;
  analysis: FailAnalysis | null;
}): FailAnalysis | null {
  if (!run.result || run.result.completed) return null;
  if (run.analysis) return run.analysis;
  return {
    pattern: run.result.reason === "timeout" ? "slow-timeout" : "unknown",
    progress: 0,
    surface: null,
    speedMps: 0,
    tiltDeg: 0,
    motorLoad: 0,
  };
}

const LAST_KEY = "pf.course.lastHint";

export function lastHint(): string | null {
  try {
    return window.sessionStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

export function rememberHint(key: string | null) {
  try {
    if (key) window.sessionStorage.setItem(LAST_KEY, key);
  } catch {
    /* 저장소 없음 — 반복 금지는 이번 화면 안에서만 */
  }
}

/** 커널 표면 이름 → 결과 문구 키(run.surface.*) */
export function surfaceKey(s: string | null | undefined): string {
  switch (s) {
    case "ramp":
    case "step":
    case "bridge":
    case "plateau":
    case "ground":
      return s;
    case "start":
    case "start-pad":
      return "start";
    case "finish":
    case "finish-zone":
      return "finish";
    case "platform":
    case null:
    case undefined:
      return "ground";
    default:
      return "other";
  }
}
